/**
 * Helpers for massaging the raw message list into the shape MessageHistory renders:
 * denormalize tool results onto their call messages and group consecutive
 * assistant turns into a single AssistantToolsMessage.
 */
import * as types from "types";

/**
 * Combines tool call and result messages so they render together.
 * Denormalizes result content onto the call message and hides the result.
 */
export function combineToolCallsAndResults(messages: types.Message[]) {
  // Shallow-copy tool messages before mutating them.
  let newMessages = messages.map((msg) => {
    if (types.isToolCallMessage(msg) || types.isToolResultMessage(msg)) {
      return { ...msg };
    }
    return msg;
  });

  let toolCallIdToMessage: Record<string, types.ToolCallMessage> = {};
  for (let msg of newMessages) {
    if (types.isToolCallMessage(msg)) {
      toolCallIdToMessage[msg.id] = msg;
    }
  }

  for (let msg of newMessages) {
    if (types.isToolResultMessage(msg)) {
      let callingMsg = toolCallIdToMessage[msg.tool_call_id];
      if (callingMsg !== undefined) {
        callingMsg.toolResult = msg.content;
        callingMsg.isError = msg.isError;
        callingMsg.task_id = msg.task_id;
        callingMsg.task_poll_interval = msg.task_poll_interval;
        callingMsg.task_name = msg.task_name;
        // Hint: comment this out to render separate tool result messages
        // (useful when modifying how orphan tool result messages are displayed)
        msg.hide = true;
      } else {
        console.warn(
          `ToolResultMessage without corresponding ToolCallMessage! ${msg}`
        );
      }
    }
  }

  return newMessages;
}

/**
 * Groups assistant response messages and tool calls from the same completion
 * request into a single `AssistantToolsMessage`.
 */
export function combineAssistantAndTools(messages: types.Message[]) {
  let newMessages = [];
  let assistantBuffer: null | types.AssistantToolsMessage = null;

  const addToAssistant = (msg: types.Message) => {
    if (assistantBuffer === null) {
      assistantBuffer = {
        // We need a stable UUID for this message.
        uuid: `AssistantToolsMessage:${msg.uuid}`,
        role: "assistant",
        content: "",
        subMessages: [msg],
      };
      newMessages.push(assistantBuffer);
      return;
    }
    assistantBuffer.subMessages.push(msg);
  };

  for (const msg of messages) {
    if (types.isToolCallMessage(msg)) {
      addToAssistant(msg);
    } else if (types.isAssistantResponseMessage(msg)) {
      addToAssistant(msg);
    } else if (types.isUserMessage(msg)) {
      // User message ends the current assistant turn.
      assistantBuffer = null;
      newMessages.push(msg);
    } else {
      newMessages.push(msg);
    }
  }

  return newMessages;
}

export function prepMessagesForRendering(messages: types.Message[]) {
  const combinedToolCalls = combineToolCallsAndResults(messages);
  const combinedMessages = combineAssistantAndTools(combinedToolCalls);
  return combinedMessages.filter((msg) => !msg.hide);
}
